import { useEffect, useState } from 'react';

import { Steps } from '@/api/types';
import { useAdditionalInfoContext } from '@/features/v2/lib/additionalInfo';
import { useWizard } from '@/features/v2/lib/wizard';

import constate from 'constate';
import { useFormContext } from 'react-hook-form';

export const [StepperProvider, useStepper] = constate(
  ({
    defaultStep = 0,
    defaultInnerStep = 0,
    defaultstepTag = null,
    innerObject,
  }: {
    defaultStep?: number;
    defaultInnerStep?: number;
    innerObject?: { [key: number]: number };
    defaultstepTag?: Steps | null;
  }) => {
    const [step, setStep] = useState(defaultStep);
    const [isDeadEnd, setIsDeadEnd] = useState(false);
    const [innerStep, setInnerStep] = useState(
      innerObject || {
        [step]: defaultInnerStep,
      }
    );

    const [stepTag, setStepTag] = useState(defaultstepTag);

    const [progress, setProgress] = useState(0);

    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [forceDisableButton, setForceDisableButton] = useState(false);

    const { allSteps } = useWizard();
    const currentStep = allSteps ? allSteps[step] : null;
    const deadEnds = currentStep?.deadEnds;

    const methods = useFormContext();
    const values = methods.watch();

    const prevInnerStep = () => {
      setInnerStep((prev) => {
        const current = prev[step] ?? 0;
        if (current === 0) return prev;
        return { ...prev, [step]: current - 1 };
      });
    };

    const nextInnerStep = () => {
      setInnerStep((prev) => {
        const currentInnerStep = { ...prev, [step]: (prev[step] ?? 0) + 1 };
        return currentInnerStep;
      });
    };

    const prevStep = () => {
      setStep((prev) => {
        if (prev === 0) return prev;
        return prev - 1;
      });
    };

    const nextStep = () => {
      setStep((prev) => {
        const nextStep = prev + 1;
        setInnerStep((prev) => {
          const currentInnerStep = { ...prev, [nextStep]: 0 };
          return currentInnerStep;
        });
        return nextStep;
      });
    };

    const { additionalInfo } = useAdditionalInfoContext();

    useEffect(() => {
      const points = currentStep?.progressPoints?.(values, additionalInfo);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const getNestedValue = (obj: Record<any, any>, path: string) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      };

      const subSteps = currentStep?.subSteps;

      if (points && !subSteps) {
        const progress = points.reduce((acc, point) => {
          if (values[point]) {
            return acc + 1;
          }
          if (getNestedValue(values, point)) {
            return acc + 1;
          }
          return acc;
        }, 0);

        setProgress((progress / points.length) * 100);
      }

      if (subSteps && !points) {
        const currentInnerStep = innerStep[step];
        const innerStepLength = subSteps(values)?.length ?? 1;

        setProgress((currentInnerStep / innerStepLength) * 100);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    useEffect(() => {
      if (!deadEnds) {
        setIsButtonDisabled(false);
        return;
      }

      const parsedDeadEnds =
        typeof deadEnds === 'object'
          ? Object.entries(deadEnds).map(
              ([_, { condition, returnToStep }]) => ({
                condition,
                returnToStep: returnToStep,
              })
            )
          : Object.entries(deadEnds(additionalInfo)).map(
              ([_, { condition, returnToStep }]) => ({
                condition,
                returnToStep: returnToStep,
              })
            );

      let isDeadEndDetected = false;
      let newReturnToStep: null | number = null;

      for (const { condition, returnToStep } of parsedDeadEnds) {
        if (condition(values, innerStep[step])) {
          isDeadEndDetected = true;
          if (innerStep[step] !== returnToStep) {
            newReturnToStep = returnToStep;
          }
          break; // Exit loop once a dead end is found and processed
        }
      }
      if (isDeadEndDetected) {
        if (newReturnToStep !== null) {
          setInnerStep((prev) => ({ ...prev, [step]: newReturnToStep! }));
        }
        if (!isButtonDisabled) {
          setIsButtonDisabled(true);
        }
        !isDeadEnd && setIsDeadEnd((prev) => !prev);
      } else {
        if (isButtonDisabled) {
          setIsButtonDisabled(false);
        }
        isDeadEnd && setIsDeadEnd((prev) => !prev);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [innerStep, isButtonDisabled, step, values, deadEnds, additionalInfo]);

    return {
      innerObject: innerStep,
      innerStep: innerStep[step] ?? 0,
      nextInnerStep,
      prevInnerStep,
      step,
      currentStep,
      nextStep,
      prevStep,
      isButtonDisabled,
      isDeadEnd,
      setStep,
      setInnerStep,
      progress: progress > 100 ? 100 : progress,
      forceDisableButton,
      setForceDisableButton,
      stepTag,
      setStepTag,
    };
  }
);
